// A per-setup history log — separate from everything else in this app in
// two ways at once: it lives entirely server-side (no local/offline
// buffering, unlike sight_settings/marks — see server/db.js's setup_log
// table), and it's an append-only audit trail, never touched by undo/redo
// (which only ever covers sight_settings/marks — see state.js's
// snapshotInputs). Three kinds of entries land here, all through the same
// POST /setups/:id/log endpoint:
//   'calc'        — auto-logged every time a compute finishes (velocity +
//                    settings + marks), coalesced server-side into one row
//                    per minute of active editing rather than one per
//                    compute.
//   'note'         — the user explicitly typing something into the History
//                    panel and hitting "Add Note".
//   'description'  — an edit to the setup's own description field. Unlike
//                    sight_settings/marks (which auto-sync as you type,
//                    see sync.js), description only ever pushes/logs when
//                    the Setup Description panel's own Save button is
//                    clicked — see logDescriptionIfChanged below.
//
// Only ever active for a logged-in session (auth.js's isLoggedIn) — like
// sync.js, every feature here is additive on top of the app's normal
// fully-offline operation, never required for it.

import { feet_to_m } from './calc.js';
import { useAuth } from './auth.js';
import { useShared, onComputeFinished, onSetupLoaded } from './state.js';
import { useSetups } from './setups.js';
import { apiFetch } from './sync.js';

const { inputs, computedResult } = useShared();
const { activeSetupId } = useSetups();

function postLog(setupId, kind, data) {
  return apiFetch(`/setups/${encodeURIComponent(setupId)}/log`, {
    method: 'POST',
    body: JSON.stringify({ kind, data }),
  });
}

export async function fetchLog(setupId) {
  const { entries } = await apiFetch(`/setups/${encodeURIComponent(setupId)}/log`);
  return entries;
}

export function addNote(setupId, text) {
  return postLog(setupId, 'note', { text });
}

// Only 'note' entries are ever editable/deletable — the server enforces
// this too (see server/index.js's requireOwnedNote), calc/description
// entries are an automatic record of what actually happened, not something
// to rewrite after the fact.
export function updateNote(setupId, entryId, text, createdAt) {
  return apiFetch(`/setups/${encodeURIComponent(setupId)}/log/${encodeURIComponent(entryId)}`, {
    method: 'PUT',
    body: JSON.stringify({ text, created_at: createdAt }),
  });
}

export function deleteNote(setupId, entryId) {
  return apiFetch(`/setups/${encodeURIComponent(setupId)}/log/${encodeURIComponent(entryId)}`, {
    method: 'DELETE',
  });
}

// ── Auto-log a calc snapshot every time a compute finishes ──
onComputeFinished(() => {
  const { isLoggedIn } = useAuth();
  const setupId = activeSetupId.value;
  if (!isLoggedIn.value || !setupId) return;

  const { updated_at, ...settings } = inputs.sight_settings;
  const marks = inputs.marks.map(({ updated_at, ...rest }) => rest);
  const data = {
    velocity: computedResult.value.v / feet_to_m, // fps, matching every other velocity readout in the app
    settings,
    marks,
  };

  postLog(setupId, 'calc', data).catch((err) => {
    console.warn('Failed to log calc snapshot:', err.message);
  });
});

// ── Log a description edit, but only when explicitly saved ──
//
// Keyed per setup id — inputs.description only ever holds whichever setup
// is *currently* active, so the baseline has to track each setup
// separately rather than a single shared value (otherwise switching setups
// would make the newly-loaded one look like an unsaved edit against the
// previous setup's baseline).
const lastLoggedDescription = {};

onSetupLoaded((setupId) => {
  lastLoggedDescription[setupId] = inputs.description;
});

// history_panel.vue subscribes here so an already-open History panel
// refreshes right away when a description gets logged elsewhere (the Setup
// Description and History panels sit in separate accordion groups — see
// app.vue's ACCORDION_GROUPS — so both can be open at once), rather than
// only picking up the new entry the next time it happens to remount.
// Returns an unsubscribe function since, unlike this module's other
// listener sets, the one caller here mounts/unmounts with the panel.
const descriptionLoggedListeners = new Set();
export function onDescriptionLogged(fn) {
  descriptionLoggedListeners.add(fn);
  return () => descriptionLoggedListeners.delete(fn);
}

// Called by setup_description.vue's Save button — logs the current
// description only if it actually differs from what was last logged for
// this setup, so clicking Save with no real edit doesn't add a no-op entry.
export function logDescriptionIfChanged(setupId) {
  const text = inputs.description;
  if (text === lastLoggedDescription[setupId]) return Promise.resolve();
  lastLoggedDescription[setupId] = text;
  return postLog(setupId, 'description', { text }).then(() => {
    descriptionLoggedListeners.forEach((fn) => fn(setupId));
  });
}
